import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { IntelligenceTypes } from '@mono/proto-clients';
import { Label, SpecraBadge, SpecraButton, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraTextarea, SpecraTooltip, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Info } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { useTablePreferences } from '../../../hooks/useTablePreferences';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { artifactClient } from '../../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../../lib/uploadAttachment';
import { DocumentsPanel } from '../../Capture/DocumentsPanel';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { fromSelectValue, labelForOption, toSelectValue, withNoneOption, withStoredFallback } from '../_shared/api/selectOptions';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { ContactPicker } from '../_shared/components/ContactPicker';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { IntelligenceFailureNotice } from '../_shared/components/IntelligenceFailureNotice';
import { EmptyState } from '../_shared/components/PageStates';
import { ProposalReview } from '../_shared/components/ProposalReview';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { WAIVERS_TAILORING_COPY } from '../_shared/constants/sectionCopy';
import { useAwaitingReviewRuns, useReextractIntelligence } from '../_shared/hooks/intelligenceHooks';
import { useCmAccess } from '../Deliverables/access';
import { DateWaiversSection, useDateWaiverCount } from '../Deliverables/components/AwaitingApproval';
import { CmStatusBadge } from '../Deliverables/components/common';
import { draftFromRule, fieldsFromDraft } from '../Deliverables/components/RuleEditor';
import { OccurrenceWindow, useAwaitingApproval, useDuties, useDuty, useMoveOccurrence, useOccurrenceDecisions, useOccurrences, useProposeExtension, useProposeWaiver, useSetDutyAssignment, useWorkstreamDetails } from '../Deliverables/hooks';
import { cmDate, cmMonth, instantDay, todayISO } from '../Deliverables/lib/format';
import { describeRule, mintReceipt } from '../Deliverables/lib/rule';
import { occurrenceStatus } from '../Deliverables/lib/status';
import { useUserNames } from '../Deliverables/lookups';
import { useContractCLINs } from '../Programs/contractHooks';
import { PROGRAM_WRITE_DENIED, useProgramWriteAccess } from '../Programs/programWriteAccess';
import { useTenantEnumValues } from '../Programs/referenceDataHooks';
import { programContactHooks } from '../Programs/relationshipHooks';
import { deviationHooks } from './hooks';
// Values MUST match the pangaea chk_deviations_type constraint
// (WAIVER/TAILORING) — sending title-case 500s every UpsertDeviation write.
// The stored values stay WAIVER/TAILORING — only the labels change (matt,
// 2026-08-27). The wire vocabulary is load-bearing well beyond this form:
// pangaea's cadence and replacement-date activation both gate on
// type = 'WAIVER', as does taskWaivers.isApprovedWaiver on the read side, and
// every row already in the database carries the old value. Renaming the
// display is a copy change; renaming the value would be a migration plus a
// coordinated backend release.
const typeOptions = [
    {
        value: 'WAIVER',
        label: 'Government Approved Change'
    },
    {
        value: 'TAILORING',
        label: 'Internal Process Change'
    }
];
// Category is the second half of matt's "the combination of Type and Category
// should essentially determine what the rest of the form allows the user to
// do/input". A Government Approved Change picks one of three; an Internal
// Process Change has only Miscellaneous today.
const CATEGORY_DUE_DATE = 'Deliverable Due Date Change';
const CATEGORY_CADENCE = 'Deliverable Cadence Change';
const CATEGORY_MISC = 'Miscellaneous';
const categoryOptionsByType = {
    WAIVER: [
        CATEGORY_DUE_DATE,
        CATEGORY_CADENCE,
        CATEGORY_MISC
    ],
    TAILORING: [
        CATEGORY_MISC
    ]
};
// The Type/Category matrix, in one place so the create modal and the drawer
// edit cannot drift. Only a Government Approved Change reaches a contract
// deliverable at all — an Internal Process Change is a comment, a state and
// its proof, and never modulates a schedule.
function isWaiverType(type) {
    return type === 'WAIVER';
}
// Stored value -> the name users now see. Falls back to the raw value so a row
// carrying anything unexpected still renders something truthful.
function typeLabel(type) {
    return typeOptions.find((o)=>o.value === type)?.label ?? type ?? '—';
}
// A category the matrix actually knows. Anything else — the free text every
// row written before this change carries, or whatever the approval-evidence
// extractor read off an email — is UNCLASSIFIED, and deliberately keeps the
// pre-change field set. Hiding fields on a legacy record would make its stored
// occurrence, replacement date or cadence unreachable and un-editable, which
// is a worse outcome than showing one field too many.
function isStandardCategory(category) {
    return category === CATEGORY_DUE_DATE || category === CATEGORY_CADENCE || category === CATEGORY_MISC;
}
// Due-date changes name ONE dated occurrence and move it. Matt: "as you can
// see the approved cadence is unnecessary for just a due date change".
function showsOccurrence(type, category) {
    if (!isWaiverType(type)) return false;
    return isStandardCategory(category) ? category === CATEGORY_DUE_DATE : true;
}
// Cadence changes re-project the whole series forward. Matt: "the occurrence
// and approved replacement date are not relevant to a pure cadence change".
function showsCadence(type, category) {
    if (!isWaiverType(type)) return false;
    return isStandardCategory(category) ? category === CATEGORY_CADENCE : true;
}
const stateOptions = [
    {
        value: 'Requested',
        label: 'Requested'
    },
    {
        value: 'Approved',
        label: 'Approved'
    },
    {
        value: 'Denied',
        label: 'Denied'
    }
];
// Recurrences a Waiver may approve for its linked deliverable (SAD-165).
// MUST stay in step with ryugu's deviationCadenceVocabulary, which rejects
// anything outside this set, and with pangaea's recurrence engine, which is
// what actually steps them. Deliberately the Add/Edit Task form's Frequency
// list minus 'One Time' — a waiver changes how often a recurring deliverable
// repeats; collapsing a live series to one occurrence is a different
// operation with different consequences for delivered history.
const DEVIATION_CADENCE_OPTIONS = [
    'Weekly',
    'Biweekly',
    'Monthly',
    'Quarterly',
    'Annually'
];
// The recorded cadence onto a team rule's cadence (ScheduleRuleFields.cadence,
// lib/rule.ts CADENCES). Anything without an entry here is refused, never
// guessed: a record written before the vocabulary closed can hold free text.
const TEAM_RULE_CADENCE = {
    weekly: 'weekly',
    biweekly: 'biweekly',
    monthly: 'monthly',
    quarterly: 'quarterly',
    annually: 'annually'
};
function teamRuleCadence(approved) {
    return TEAM_RULE_CADENCE[approved.trim().toLowerCase()];
}
// A deliverable of the contract model, as a person reads it (never an id).
function dutyLabel(d) {
    return `${d.title || 'Untitled deliverable'}${d.pwsRef ? ` · PWS ${d.pwsRef}` : ''}`;
}
// One date of a deliverable: "February 2026 · due 2 Feb 2026 · Late, 3 days".
function occurrenceLabel(o) {
    const period = o.label || cmMonth(o.nominalDueOn || o.dueOn);
    return [
        period,
        `due ${cmDate(o.dueOn, 'not stated')}`,
        occurrenceStatus(o).label
    ].filter(Boolean).join(' · ');
}
// The reason a waiver proposal or a date move starts from: what this record
// says about itself. Editable before it is sent.
function deviationReason(d) {
    return [
        d.category,
        d.comment
    ].filter(Boolean).join(': ');
}
const NO_DATE_LABEL = 'No single date';
// Values MUST match the pangaea chk_deviations_approval_source constraint
// (CO/COR/BOTH). This role code is kept alongside the picked person (#2).
const approvalSourceOptions = [
    {
        value: 'CO',
        label: 'Contracting Officer (CO)'
    },
    {
        value: 'COR',
        label: 'Contracting Officer Rep (COR)'
    },
    {
        value: 'BOTH',
        label: 'Both (CO & COR)'
    }
];
// A proposal's value is always a stringValue for this workflow (DeviationExtractor
// only ever builds proposals via structpb.NewStringValue) — a minimal local
// unwrap rather than importing ProposalReview's own (unexported) unwrapValue.
function unwrapDeviationString(v) {
    return v?.kind.case === 'stringValue' ? v.kind.value : undefined;
}
// Bindings for every destination DeviationContract declares (deviation_extractor.go).
// APPROVED_BY_CONTACT_NAME, ANSWERED_AT, LINKED_CLIN_ID and LINKED_TASK_ID don't
// map onto a plain RHF field — see applyDeviationProposals, which special-cases
// each of those `formField` tokens instead of calling form.setValue with them.
// APPROVED_BY_CONTACT_ID is deliberately absent: this extractor never proposes
// it (see DeviationContract's own comment), so there is nothing to bind.
const DEVIATION_FIELD_BINDINGS = {
    [IntelligenceTypes.DestinationFieldName.DEVIATION_TYPE]: {
        formField: 'type',
        label: 'Type'
    },
    [IntelligenceTypes.DestinationFieldName.CATEGORY]: {
        formField: 'category',
        label: 'Category'
    },
    [IntelligenceTypes.DestinationFieldName.COMMENT]: {
        formField: 'comment',
        label: 'Comment'
    },
    [IntelligenceTypes.DestinationFieldName.DEVIATION_STATE]: {
        formField: 'state',
        label: 'State'
    },
    [IntelligenceTypes.DestinationFieldName.APPROVAL_SOURCE]: {
        formField: 'approvalSource',
        label: 'Approval source'
    },
    [IntelligenceTypes.DestinationFieldName.APPROVED_BY_CONTACT_NAME]: {
        formField: 'approverNameSuggestion',
        label: 'Approved by (name from evidence)'
    },
    [IntelligenceTypes.DestinationFieldName.ANSWERED_AT]: {
        formField: 'answeredAtSuggestion',
        label: 'Answer received',
        // The extractor proposes a UTC RFC 3339 instant; the field itself only
        // ever stores a calendar day (see WAIVERS_TAILORING_COPY.answeredAtHelp).
        parse: (v)=>typeof v === 'string' ? v.slice(0, 10) : v
    },
    [IntelligenceTypes.DestinationFieldName.LINKED_CLIN_ID]: {
        formField: 'linkedClinReferenceSuggestion',
        label: 'Linked CLIN (unresolved reference)'
    },
    [IntelligenceTypes.DestinationFieldName.LINKED_TASK_ID]: {
        formField: 'linkedTaskReferenceSuggestion',
        label: 'Linked deliverable (unresolved reference)'
    }
};
// Applies accepted proposals to the draft. Real RHF fields (type/category/
// comment/state/approvalSource) go through form.setValue; the
// answeredAtSuggestion *token* writes the real out-of-form answeredAt state
// (no authorization concern — it's a deterministic header fact, so it isn't
// a "suggestion" the way the approver name is). approverNameSuggestion is
// NEVER written into approvalContactId/approvalContactName: it only feeds a
// read-only hint, so the reviewer must still make an explicit pick in the
// contact picker (the approver-identity gate this component enforces below).
// The two unresolved-reference suggestions are intentionally no-ops here —
// ProposalReview already renders their mandatory warning, and applying a raw
// reference into the real linked CLIN/deliverable select would just fail
// to match any option anyway.
function applyDeviationProposals(values, setValue, setAnsweredAt, setApproverNameSuggestion) {
    for (const { formField, value } of values){
        if (formField === 'answeredAtSuggestion') {
            if (typeof value === 'string') setAnsweredAt(value);
        } else if (formField === 'approverNameSuggestion') {
            if (typeof value === 'string') setApproverNameSuggestion(value);
        } else if (formField === 'linkedClinReferenceSuggestion' || formField === 'linkedTaskReferenceSuggestion') {
        // no-op — see doc comment above.
        } else {
            setValue(formField, value);
        }
    }
}
// State → semantic tone. A requested deviation is unresolved drift (warning);
// approved is a granted departure (success); denied is a hard no (danger).
const DEVIATION_STATE_TONE = {
    Requested: 'warning',
    Approved: 'success',
    Denied: 'danger'
};
// `clinLabel` resolves a row's linked CLIN id to its number — matt asked to
// "show which CLIN each record is for on the main screen for this section...
// so you can find which one you want to if you need to look for a certain
// CLINs", so the column is built per-render from the program's CLIN list
// rather than being a static config like the rest.
function buildColumns(clinLabel) {
    return [
        {
            key: 'type',
            header: 'Type',
            sortable: true,
            filter: 'select',
            filterOptions: typeOptions,
            render: (d)=>typeLabel(d.type)
        },
        {
            key: 'category',
            header: 'Category',
            sortable: true,
            filter: 'text'
        },
        {
            key: 'linkedClinId',
            header: 'CLIN',
            sortable: true,
            render: (d)=>/*#__PURE__*/ _jsx("span", {
                    className: "font-mono text-[length:var(--fs-ui-body)]",
                    children: clinLabel(d.linkedClinId)
                })
        },
        {
            key: 'state',
            header: 'State',
            sortable: true,
            filter: 'select',
            filterOptions: stateOptions,
            render: (d)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: DEVIATION_STATE_TONE[d.state] ?? 'neutral',
                    children: d.state || '—'
                })
        },
        {
            key: 'approvalSource',
            header: 'Approval source'
        }
    ];
}
// Per-option Type guidance, shared by the create form and the drawer edit
// (comment #2 on PR 972: the drawer built its own Type editField without this,
// so the Waiver/Tailoring definitions never appeared there) — built once so
// the two configurations cannot drift.
const typeOptionHelp = [
    {
        label: 'Government Approved Change',
        text: WAIVERS_TAILORING_COPY.typeHelp.WAIVER
    },
    {
        label: 'Internal Process Change',
        text: WAIVERS_TAILORING_COPY.typeHelp.TAILORING
    }
];
// Linked CLIN / Linked deliverable / occurrence are NOT declared here even
// though they're conceptually part of this form — see linkedClinId's state
// declaration below for why (a three-tier dependent select needs a live
// watched value a static FormFieldConfig list can't react to).
// `storedCategory` keeps a record's existing free-text category selectable
// after the vocabulary closed: every row written before this change carries
// arbitrary text, and dropping it from the options would silently rewrite the
// category of any such record the user merely opened and re-saved.
function fieldsFor(storedType) {
    // Scoped to the record's own type when there is one; the create modal starts
    // with no type chosen, so it suggests the union.
    const suggestions = storedType ? categoryOptionsByType[storedType] ?? [] : [
        CATEGORY_DUE_DATE,
        CATEGORY_CADENCE,
        CATEGORY_MISC
    ];
    return [
        {
            kind: 'select',
            name: 'type',
            label: 'Type',
            required: true,
            options: typeOptions,
            // Per-option guidance rendered beneath the control (not inside the
            // dropdown — see the optionHelp comment on SelectFieldConfig).
            optionHelp: typeOptionHelp,
            optionHelpTestId: Testids.deliveryCompliance.deviationTypeHelp
        },
        {
            // Deliberately a suggestion list rather than a closed select. The three
            // standard categories are what matt asked users to pick from, and they
            // are what the Type/Category matrix below keys on — but the field also
            // has to keep holding two kinds of value it does not control: the free
            // text every row written before this change already carries, and
            // whatever the approval-evidence extractor reads off an email. A closed
            // select would silently blank both.
            kind: 'text',
            name: 'category',
            label: 'Category',
            required: true,
            maxLength: 200,
            suggestions,
            helpText: WAIVERS_TAILORING_COPY.categoryHelpText
        },
        {
            kind: 'textarea',
            name: 'comment',
            label: 'Comment',
            rows: 3
        },
        {
            kind: 'select',
            name: 'state',
            label: 'State',
            required: true,
            options: stateOptions
        },
        {
            kind: 'select',
            name: 'approvalSource',
            label: 'Approval source',
            options: approvalSourceOptions,
            helpText: WAIVERS_TAILORING_COPY.approvalSourceHelp
        }
    ];
}
export function DeviationsTab({ programId }) {
    // Upsert/DeleteDeviation: update + area:quality. canEdit is update alone.
    // Waiving or moving the linked date, and the team rule, are contract-model
    // writes (update + area:execution). Attachments ride tesoro
    // LinkArtifactToEntity (same as the create-form stage), not CreateProgramDocument.
    const canWrite = useProgramWriteAccess(programId).quality;
    const cm = useCmAccess(programId);
    const { user } = useAuth();
    const { data, isLoading } = deviationHooks.useList({
        programId
    });
    const upsert = deviationHooks.useUpsert();
    const remove = deviationHooks.useDelete();
    const prefs = useTablePreferences('ts:delivery:deviations');
    const items = data?.items ?? [];
    const waivers = useDateWaiverCount(programId);
    // Proposals to excuse one deliverable date wait here too (SAD-823).
    const dateWaiversAwaiting = (useAwaitingApproval(programId).query.data?.decisions ?? []).filter((d)=>d.kind === 'waiver' || d.kind === 'extension').length;
    const awaiting = items.filter((d)=>d.state === 'Requested').length + dateWaiversAwaiting;
    const approved = items.filter((d)=>d.state === 'Approved').length;
    const denied = items.filter((d)=>d.state === 'Denied').length;
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // The CO/COR approval contact is parent-controlled (like the risk owner): the
    // picker fills the id + name, kept out of the RHF form.
    const [approvalContactId, setApprovalContactId] = useState('');
    const [approvalContactName, setApprovalContactName] = useState('');
    // The approved replacement due date (SAD-165) is parent-controlled like the
    // approval contact above, not an RHF field — extraContent's watch('type')
    // needs to gate its visibility on the LIVE type value, which a declarative
    // FormFieldConfig can't react to. '' means no replacement date.
    const [replacementDueDate, setReplacementDueDate] = useState('');
    // Linked CLIN -> Linked deliverable -> date are ALSO parent-controlled
    // state, for the same reason as replacementDueDate above: the deliverable
    // select must filter to the LIVE clin pick, and the date select must list
    // the LIVE deliverable's dates, both of which require reading another
    // field's current value — something a declarative FormFieldConfig, built
    // before the RHF form even exists, cannot do. Clearing a parent clears its
    // dependents (below) so the chain can never submit a date that is not the
    // named deliverable's, which pangaea refuses (D113).
    const [linkedClinId, setLinkedClinId] = useState('');
    const [linkedDutyId, setLinkedDutyId] = useState('');
    const [linkedOccurrenceId, setLinkedOccurrenceId] = useState('');
    // The old model's deliverable link, no longer offered. Carried back
    // unchanged on save (a plain string on the wire, so omitting it would
    // clear it), and it still gates the recorded cadence, which ryugu only
    // accepts together with it.
    const [legacyTaskId, setLegacyTaskId] = useState('');
    // The cadence a Waiver approves for the linked deliverable (SAD-165, matt
    // 2026-08-27) — same parent-controlled pattern and same reason as
    // replacementDueDate above: extraContent gates its visibility on the LIVE
    // type value. '' means this waiver approves no cadence change.
    const [approvedCadence, setApprovedCadence] = useState('');
    // When the approval answer (Approved/Denied) came back (SAD-172) — same
    // out-of-form, parent-controlled pattern as replacementDueDate above, and
    // same reason: the full-replace upsert must ALWAYS send a value (a real
    // date or the explicit-clear sentinel), never omit the field, or a value
    // cleared on one surface would silently survive on the other. Unlike
    // replacementDueDate this is unconditional — not gated on type.
    const [answeredAt, setAnsweredAt] = useState('');
    // Files staged in the New form; attached to the deviation on save (#1). Only
    // the create form stages files — the drawer edits via the live DocumentsPanel.
    const [stagedFiles, setStagedFiles] = useState([]);
    // Approval-evidence review (SAD-172). draftId is regenerated per dialog open
    // (mirrors CPARSScoresTab's openNew) — ProposalReview passes it through
    // unchanged to MarkRunAppliedToDraft, an audit transition only. The upload
    // cache is keyed by file identity so re-processing without re-staging a new
    // file never re-uploads the same bytes.
    const [draftId, setDraftId] = useState('');
    // The intelligence run THIS dialog session actually started via
    // processApprovalEvidence below (SAD-235) — never derived from
    // deviationRuns[0], which can be an arbitrary/older run still awaiting
    // review against the same shared (programId, Deviation, '') target left
    // over from a different dialog session. Reset alongside draftId below.
    const [pinnedDeviationRunId, setPinnedDeviationRunId] = useState('');
    const [processingApproval, setProcessingApproval] = useState(false);
    const [processApprovalError, setProcessApprovalError] = useState(null);
    const uploadedApprovalRef = useRef(null);
    // Read-only hint from APPROVED_BY_CONTACT_NAME — deliberately never fed into
    // approvalContactId/approvalContactName (see applyDeviationProposals' doc
    // comment); it only points the reviewer at the picker.
    const [approverNameSuggestion, setApproverNameSuggestion] = useState('');
    const reextract = useReextractIntelligence();
    // programId collapses to '' while the create dialog is closed, which
    // disables useAwaitingReviewRuns' query (it gates on !!target.programId) —
    // this dialog is the only place a Deviation run is ever created (this
    // EntityFormDialog only ever creates, never edits — see submitDeviation).
    const deviationIntelligenceTarget = useMemo(()=>({
            programId: formOpen ? programId : '',
            entityType: 'Deviation',
            entityId: ''
        }), [
        formOpen,
        programId
    ]);
    const { data: deviationRuns } = useAwaitingReviewRuns(IntelligenceTypes.WorkflowKind.DEVIATION, deviationIntelligenceTarget);
    // Pinned to the run THIS dialog started (pinnedDeviationRunId above) —
    // never deviationRuns?.[0], which silently binds to whichever run happens
    // to sort first for the shared target, including one from an unrelated
    // dialog session. No match (not started yet, or not hydrated yet) means
    // "no run yet" — never a fallback to some other run.
    const deviationRun = pinnedDeviationRunId ? deviationRuns?.find((r)=>r.id === pinnedDeviationRunId) : undefined;
    const deviationApprovedStateProposal = deviationRun?.proposals.find((p)=>p.destination?.fieldName === IntelligenceTypes.DestinationFieldName.DEVIATION_STATE);
    // LOAD-BEARING per deviation_extractor.go: an "Approved" DEVIATION_STATE
    // proposal ALWAYS carries a non-empty warning naming the unverified sender.
    // Presence of that warning — not a sender-name match — is what gates Save:
    // an email alone can never itself constitute a verified approval. This
    // reads only the proposal's STABLE fields (proposedValue/warnings), which
    // never change after the run is created — never its live reviewState,
    // which the gate below deliberately does not depend on (see
    // deviationApprovalBlocked's doc comment for why: it ties the gate to
    // whether the value actually reached the form, not to review bookkeeping).
    const deviationApprovedStateHasUnverifiedWarning = !!deviationApprovedStateProposal && unwrapDeviationString(deviationApprovedStateProposal.proposedValue) === 'Approved' && deviationApprovedStateProposal.warnings.length > 0;
    // Uploads the first staged file (the attached approval email) eagerly, then
    // starts a DEVIATION intelligence run against it. entityId is always '' —
    // this dialog only ever creates a new Deviation (see submitDeviation).
    const processApprovalEvidence = async ()=>{
        const file = stagedFiles[0];
        if (!file) return;
        setProcessApprovalError(null);
        setProcessingApproval(true);
        try {
            let artifactId;
            if (uploadedApprovalRef.current?.file === file) {
                artifactId = uploadedApprovalRef.current.artifactId;
            } else {
                const tenantId = user?.tenant_id;
                if (!tenantId) throw new Error('No active tenant — cannot upload approval evidence.');
                const uploaded = await uploadAttachmentViaTesoro(file, {
                    tenantId,
                    ownerUserId: user?.id ?? '',
                    autoProcess: false
                });
                artifactId = uploaded.artifactId;
                uploadedApprovalRef.current = {
                    file,
                    artifactId
                };
            }
            const started = await reextract.reextract({
                workflowKind: IntelligenceTypes.WorkflowKind.DEVIATION,
                target: {
                    programId,
                    entityType: 'Deviation',
                    entityId: ''
                },
                sourceArtifactId: artifactId
            });
            if (started?.id) setPinnedDeviationRunId(started.id);
        } catch (err) {
            setProcessApprovalError(err instanceof Error ? err.message : 'Failed to process approval evidence');
        } finally{
            setProcessingApproval(false);
        }
    };
    const clins = useContractCLINs(programId).data?.clins ?? [];
    // CLIN id -> number, for the list's CLIN column. Falls back to an em dash
    // for a record linked to no CLIN (an Internal Process Change never is) and
    // to the raw id if the CLIN has since been removed from the program.
    const clinNumberById = useMemo(()=>{
        const m = new Map();
        for (const c of clins)m.set(c.id, c.clinNumber);
        return m;
    }, [
        clins
    ]);
    const columns = useMemo(()=>buildColumns((clinId)=>clinId ? clinNumberById.get(clinId) ?? clinId : '—'), [
        clinNumberById
    ]);
    const clinOptions = withNoneOption(clins.map((c)=>({
            value: c.id,
            label: `${c.clinNumber} ${c.shortTitle}`.trim()
        })));
    // The contract model's deliverables (D113), with their rules so the CLIN
    // pick can scope them: a deliverable is on a CLIN when one of its rules is.
    const dutiesData = useDuties(programId, {
        includeRules: true
    }).data;
    const duties = useMemo(()=>dutiesData?.duties ?? [], [
        dutiesData
    ]);
    const dutyById = useMemo(()=>new Map(duties.map((d)=>[
                d.id,
                d
            ])), [
        duties
    ]);
    const clinsByDuty = useMemo(()=>{
        const m = new Map();
        for (const r of dutiesData?.rules ?? []){
            if (!r.clinId) continue;
            const set = m.get(r.dutyId) ?? new Set();
            set.add(r.clinId);
            m.set(r.dutyId, set);
        }
        return m;
    }, [
        dutiesData
    ]);
    // Deliverable options scoped to the live CLIN pick; no CLIN picked lists
    // every deliverable, so a change that never bothers with a CLIN isn't
    // forced to pick one first. A stored pick outside the scope stays listed.
    const deliverableOptions = useMemo(()=>{
        const scoped = linkedClinId ? duties.filter((d)=>clinsByDuty.get(d.id)?.has(linkedClinId)) : duties;
        const stored = linkedDutyId ? dutyById.get(linkedDutyId) : undefined;
        const list = stored && !scoped.includes(stored) ? [
            ...scoped,
            stored
        ] : scoped;
        return withStoredFallback(withNoneOption(list.map((d)=>({
                value: d.id,
                label: dutyLabel(d)
            }))), linkedDutyId);
    }, [
        duties,
        dutyById,
        clinsByDuty,
        linkedClinId,
        linkedDutyId
    ]);
    // The live deliverable's dates, for the date select.
    const pickerOccurrences = useOccurrences(programId, {
        dutyId: linkedDutyId,
        window: OccurrenceWindow.ALL
    }, {
        enabled: !!linkedDutyId
    }).data?.occurrences ?? [];
    const occurrenceOptions = useMemo(()=>[
            ...pickerOccurrences
        ].sort((a, b)=>(a.dueOn || '9999').localeCompare(b.dueOn || '9999')).map((o)=>({
                value: o.id,
                label: occurrenceLabel(o)
            })), [
        pickerOccurrences
    ]);
    // The approval-contact picker offers this program's GOVERNMENT contacts —
    // "any government affiliated contacts on the program itself. Not EVERY
    // contact, just the government ones" (matt, 2026-08-27).
    //
    // customer_poc_role is the customer-side role enum, so carrying any value
    // from it IS the government-affiliation test: CO, COR, Contract Specialist,
    // Government PM, Alternate COR, Administrative CO, Other. An earlier
    // narrowing to CO/COR alone admitted two of those seven, so a program whose
    // approving contact was an Alternate COR or a Contract Specialist saw an
    // empty dropdown and could not record who approved anything.
    //
    // Company-side contacts carry no customer_poc_role and are still excluded,
    // which is the half of matt's sentence that says "not EVERY contact".
    const pocRoles = useTenantEnumValues('customer_poc_role').data ?? [];
    const governmentPocRoleIds = useMemo(()=>new Set(pocRoles.map((r)=>r.id)), [
        pocRoles
    ]);
    const programContacts = programContactHooks.useList({
        programId
    }).data?.items ?? [];
    const contactNameById = useMemo(()=>{
        const m = new Map();
        for (const c of programContacts)m.set(c.contactId, c.contactName);
        return m;
    }, [
        programContacts
    ]);
    // The other half of the approver-identity gate (SAD-172): does the name the
    // evidence attributed the approval to match a contact this program actually
    // knows about? A match is not itself verification (a spoofed sender can
    // still name a real contact) — the gate below already requires an explicit
    // pick regardless — but a NON-match is a much louder signal that deserves
    // its own prominent warning.
    const deviationKnownContactNames = useMemo(()=>new Set(Array.from(contactNameById.values()).map((n)=>n.trim().toLowerCase())), [
        contactNameById
    ]);
    const deviationSenderIsUnknownContact = !!approverNameSuggestion && !deviationKnownContactNames.has(approverNameSuggestion.trim().toLowerCase());
    const deviationShowUnverifiedSenderBanner = deviationApprovedStateHasUnverifiedWarning && deviationSenderIsUnknownContact;
    // Seed the out-of-form approval picker from a record. Prefer the persisted
    // name; fall back to the live contact list for rows saved before the name was
    // denormalized.
    const seedApprovalFrom = (row)=>{
        setApprovalContactId(row?.approvedByContactId ?? '');
        setApprovalContactName(row?.approvedByContactName || contactNameById.get(row?.approvedByContactId ?? '') || '');
    };
    // Seed the out-of-form replacement-date state from a record — same
    // contract as seedApprovalFrom above.
    const seedReplacementDateFrom = (row)=>{
        setReplacementDueDate(formatLocalYmd(row?.approvedReplacementDueDate) ?? '');
    };
    // Seed the out-of-form CLIN/deliverable/date chain from a record — same
    // contract as seedApprovalFrom above.
    const seedLinksFrom = (row)=>{
        setLinkedClinId(row?.linkedClinId ?? '');
        setLinkedDutyId(row?.linkedDutyId ?? '');
        setLinkedOccurrenceId(row?.linkedOccurrenceId ?? '');
        setLegacyTaskId(row?.linkedTaskId ?? '');
    };
    // Seed the out-of-form approved cadence from a record — same contract as
    // seedReplacementDateFrom above.
    const seedCadenceFrom = (row)=>{
        setApprovedCadence(row?.approvedCadence ?? '');
    };
    // Seed the out-of-form answered-at state from a record — same contract as
    // seedReplacementDateFrom above.
    const seedAnsweredAtFrom = (row)=>{
        setAnsweredAt(formatLocalYmd(row?.answeredAt) ?? '');
    };
    const openCreate = ()=>{
        setEditing(null);
        seedApprovalFrom(null);
        seedReplacementDateFrom(null);
        seedLinksFrom(null);
        seedCadenceFrom(null);
        seedAnsweredAtFrom(null);
        setStagedFiles([]);
        setDraftId(crypto.randomUUID());
        setPinnedDeviationRunId('');
        setProcessingApproval(false);
        setProcessApprovalError(null);
        uploadedApprovalRef.current = null;
        setApproverNameSuggestion('');
        setFormOpen(true);
    };
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). Attachment staging is a no-op unless files are staged
    // (only the create form stages them; the drawer uses the live DocumentsPanel).
    const submitDeviation = async (values, id)=>{
        // A field the Type/Category matrix has HIDDEN must be cleared, not carried.
        // These three live in parent state, which survives their picker unmounting,
        // so a replacement date typed under Deliverable Due Date Change would
        // otherwise still be submitted — and activated server-side — after
        // switching to Deliverable Cadence Change, and a cadence picked under
        // Cadence Change would silently re-project the series after switching to
        // Due Date Change. Neither is visible at save time, so neither can be
        // corrected by the user. Hidden therefore means "send the explicit-clear
        // sentinel", the same full-replace contract the visible case already uses.
        //
        // An UNCLASSIFIED category keeps everything (see showsOccurrence), so a
        // legacy record's stored occurrence, date and cadence all survive a save.
        const keepOccurrence = showsOccurrence(values.type, values.category);
        const keepCadence = showsCadence(values.type, values.category);
        const saved = await upsert.mutateAsync({
            id,
            programId,
            type: values.type,
            category: values.category,
            comment: values.comment ?? '',
            state: values.state,
            approvalSource: values.approvalSource ?? '',
            // The old model's link goes back exactly as stored; its occurrence
            // link (linkedDeliverableInstanceId) is omitted, which keeps it.
            linkedTaskId: legacyTaskId,
            linkedClinId,
            // The contract model's links (D113), always sent: this form re-submits
            // the whole record, so a cleared pick must reach the server as ''.
            linkedDutyId,
            linkedOccurrenceId: keepOccurrence ? linkedOccurrenceId : '',
            approvedByContactId: approvalContactId,
            // Persist the name too, so it still renders if the contact is later
            // removed from the program (mirrors the risk owner name).
            approvedByContactName: approvalContactName,
            // Only meaningful for a Waiver (SAD-165) — omitted (undefined) for any
            // other type so the server's explicit-presence contract leaves an
            // existing stored date untouched rather than blanking it. For a
            // Waiver, ALWAYS send a value rather than omitting: this form fully
            // re-submits the record every save, so "the field is blank" must reach
            // the server as the explicit-clear sentinel (a present zero Timestamp),
            // never as "absent" — the latter would leave a previously-set date
            // stuck forever with no way for the user to remove it.
            approvedReplacementDueDate: values.type === 'WAIVER' ? keepOccurrence && replacementDueDate ? Timestamp.fromDate(parseLocalDate(replacementDueDate)) : new Timestamp({
                seconds: BigInt(0),
                nanos: 0
            }) : undefined,
            // Unconditional across type/state (unlike the WAIVER-only replacement
            // due date above) — ALWAYS send a value, real or the explicit-clear
            // sentinel, per the same full-replace-upsert contract.
            answeredAt: answeredAt ? Timestamp.fromDate(parseLocalDate(answeredAt)) : new Timestamp({
                seconds: BigInt(0),
                nanos: 0
            }),
            // Waiver-only, and on the same full-replace contract as the replacement
            // due date above: for a Waiver ALWAYS send a value so clearing the
            // select reaches the server as the explicit '' clear rather than as an
            // omitted field, which would strand a previously-approved cadence with
            // no way to revoke it. Omitted entirely for any other type, so a
            // Tailoring save can never blank a Waiver's stored cadence.
            approvedCadence: values.type === 'WAIVER' ? keepCadence && (legacyTaskId || linkedDutyId) ? approvedCadence : '' : undefined
        });
        // Attach staged files to the now-saved deviation (bytes via tesoro's
        // presigned PUT, then link to the deviation entity). Attachments are
        // SECONDARY to the save: the deviation is already committed, so a failure
        // here must not throw. If it did, EntityFormDialog would keep the form open
        // with `editing` still null, and a retry Save would create a DUPLICATE
        // deviation (re-uploading the files too). Instead each file is attempted
        // independently and failures are surfaced via toast; the user re-adds them
        // from the deviation's Details panel (the live DocumentsPanel).
        const deviationId = saved?.id ?? id ?? '';
        const tenantId = user?.tenant_id;
        const ownerUserId = user?.id ?? '';
        if (stagedFiles.length > 0) {
            if (!deviationId || !tenantId) {
                // The deviation itself saved, but we can't attach without its id or an
                // active tenant. Tell the user rather than dropping the files silently.
                toast.error(`Saved, but ${stagedFiles.length} attachment(s) couldn't upload. Add them from the record's Details panel.`);
            } else {
                const failed = [];
                for (const file of stagedFiles){
                    try {
                        const uploaded = await uploadAttachmentViaTesoro(file, {
                            tenantId,
                            ownerUserId,
                            autoProcess: false
                        });
                        await artifactClient.linkArtifactToEntity({
                            artifactId: uploaded.artifactId,
                            entityType: 'deviation',
                            entityId: deviationId
                        });
                    } catch  {
                        failed.push(file.name);
                    }
                }
                if (failed.length > 0) {
                    toast.error(`Saved, but ${failed.length} attachment(s) failed to upload: ${failed.join(', ')}. Add them from the record's Details panel.`);
                }
            }
        }
        setStagedFiles([]);
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The approval-contact picker — shared verbatim by the create modal
    // (extraContent) and the drawer's inline edit (the Deviation "Approved by" row
    // via editControl). Both drive the same parent state.
    const approvalPicker = /*#__PURE__*/ _jsx(ContactPicker, {
        id: "approvedByContactId",
        label: "Approval contact",
        helpText: "Who approved this change — pick from this program's government contacts.",
        programId: programId,
        value: approvalContactId,
        testid: Testids.deliveryCompliance.deviationApprovalContact,
        fallbackLabel: approvalContactName,
        filter: (c)=>governmentPocRoleIds.has(c.customerPocRoleId),
        onClear: ()=>{
            setApprovalContactId('');
            setApprovalContactName('');
        },
        onChange: (cid, name)=>{
            setApprovalContactId(cid);
            setApprovalContactName(name);
        }
    });
    // Linked CLIN -> Linked deliverable -> date, shared verbatim by the create
    // modal (extraContent) and the drawer's inline edit — same
    // parent-controlled-state pattern as replacementDueDatePicker below.
    // Picking a CLIN scopes the deliverables (deliverableOptions above) to the
    // ones with a rule on that CLIN; picking a deliverable lists its dates.
    const linkedClinPicker = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "deviation-linked-clin",
                children: "Linked CLIN"
            }),
            /*#__PURE__*/ _jsx("select", {
                id: "deviation-linked-clin",
                className: "h-9 w-full rounded-md border border-input bg-background px-3 text-sm",
                value: toSelectValue(linkedClinId),
                onChange: (e)=>{
                    setLinkedClinId(fromSelectValue(e.target.value));
                    // Changing the parent clears every dependent — never leave a
                    // deliverable/date pairing from the OLD CLIN.
                    setLinkedDutyId('');
                    setLinkedOccurrenceId('');
                },
                children: withStoredFallback(clinOptions, linkedClinId).map((o)=>/*#__PURE__*/ _jsx("option", {
                        value: o.value,
                        children: o.label
                    }, o.value))
            })
        ]
    });
    const linkedDeliverablePicker = /*#__PURE__*/ _jsx(SpecraFormField, {
        label: "Linked deliverable",
        htmlFor: "deviation-linked-deliverable",
        children: /*#__PURE__*/ _jsx(SpecraSelect, {
            id: "deviation-linked-deliverable",
            value: toSelectValue(linkedDutyId),
            onChange: (e)=>{
                setLinkedDutyId(fromSelectValue(e.target.value));
                // A date belongs to one deliverable; pangaea refuses a pairing
                // that is not.
                setLinkedOccurrenceId('');
            },
            "data-testid": Testids.cmCompliance.deviationDeliverable,
            children: deliverableOptions.map((o)=>/*#__PURE__*/ _jsx("option", {
                    value: o.value,
                    children: o.label
                }, o.value))
        })
    });
    // Only meaningful for a Government Approved Change naming a deliverable —
    // there's nothing to list before a deliverable is picked. A stored date no
    // longer listed (retired) stays selectable so an unrelated save keeps it.
    const linkedOccurrencePicker = /*#__PURE__*/ _jsx(SpecraFormField, {
        label: "Date",
        htmlFor: "deviation-linked-occurrence",
        hint: "The one date this change is about. Leave it on “No single date” when the change covers the whole deliverable.",
        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
            id: "deviation-linked-occurrence",
            value: linkedOccurrenceId,
            onChange: (e)=>setLinkedOccurrenceId(e.target.value),
            "data-testid": Testids.cmCompliance.deviationOccurrence,
            children: [
                /*#__PURE__*/ _jsx("option", {
                    value: "",
                    children: NO_DATE_LABEL
                }),
                withStoredFallback(occurrenceOptions, linkedOccurrenceId).map((o)=>/*#__PURE__*/ _jsx("option", {
                        value: o.value,
                        children: o.label
                    }, o.value))
            ]
        })
    });
    // The approved cadence. It needs a deliverable to apply to: the contract
    // model's (linkedDutyId; once Approved it applies as a team rule, D114 #4)
    // or, on an old record, the old model's task. Saving it moves nothing.
    const approvedCadencePicker = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "deviation-cadence",
                children: "Approved cadence (recorded)"
            }),
            /*#__PURE__*/ _jsxs("select", {
                id: "deviation-cadence",
                className: "h-9 w-full rounded-md border border-input bg-background px-3 text-sm",
                value: approvedCadence,
                onChange: (e)=>setApprovedCadence(e.target.value),
                "data-testid": Testids.deliveryCompliance.deviationCadence,
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "No cadence change"
                    }),
                    DEVIATION_CADENCE_OPTIONS.map((c)=>/*#__PURE__*/ _jsx("option", {
                            value: c,
                            children: c
                        }, c))
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Recorded on this change. Once the change is approved, apply it as a team rule from its details; saving it here does not move any date."
            })
        ]
    });
    // The replacement due date — a recorded field. The old model's schedule
    // read it; the deliverable screens do not, so it claims to move nothing.
    // Once a date is linked the drawer offers to move that date to it.
    // Clearable — clearing sends the server's explicit-clear sentinel on save
    // (see submitDeviation), not an omitted field.
    const replacementDueDatePicker = /*#__PURE__*/ _jsx("div", {
        className: "space-y-1.5",
        children: /*#__PURE__*/ _jsx(SpecraDateField, {
            label: "Approved replacement due date (recorded)",
            hint: "Recorded on this change for reference; saving it does not move the deliverable’s date. With a date linked, move it from this record’s details.",
            value: replacementDueDate,
            onValueChange: setReplacementDueDate,
            "data-testid": Testids.deliveryCompliance.deviationReplacementDueDate
        })
    });
    // The answered-at picker (SAD-172) — shared verbatim by the create modal
    // (extraContent) and the drawer's inline edit. Unlike the replacement due
    // date above, this applies to every Type/State: it only records when the
    // approval answer came back and never affects a task's due date.
    const answeredAtPicker = /*#__PURE__*/ _jsx("div", {
        className: "space-y-1.5",
        children: /*#__PURE__*/ _jsx(SpecraDateField, {
            label: "Answer received",
            hint: WAIVERS_TAILORING_COPY.answeredAtHelp,
            value: answeredAt,
            onValueChange: setAnsweredAt,
            "data-testid": Testids.deliveryCompliance.deviationAnsweredAt
        })
    });
    // Build the deviation form config (fields + defaults) for a record — reused by
    // the create modal (editing) and the drawer edit (detail).
    const deviationFormConfig = (r)=>({
            fields: fieldsFor(r?.type),
            defaultValues: {
                id: r?.id ?? '',
                type: r?.type ?? '',
                category: r?.category ?? '',
                comment: r?.comment ?? '',
                state: r?.state ?? 'Requested',
                approvalSource: r?.approvalSource ?? ''
            }
        });
    // The read-view "Approved by" name for the open record.
    const detailApprovedByName = detail ? detail.approvedByContactName || contactNameById.get(detail.approvedByContactId ?? '') || '' : '';
    // The drawer's inline-edit config, bound to the record being viewed.
    const detailEdit = detail ? {
        defaultValues: deviationFormConfig(detail).defaultValues,
        onSubmit: (v)=>submitDeviation(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>{
            seedApprovalFrom(detail);
            seedReplacementDateFrom(detail);
            seedLinksFrom(detail);
            seedCadenceFrom(detail);
            seedAnsweredAtFrom(detail);
            // The drawer never stages files (attachments go through the live
            // DocumentsPanel), so clear any left over from a canceled create.
            setStagedFiles([]);
        }
    } : undefined;
    // The open record's linked date, from its deliverable's dates (the same
    // query the date picker runs while editing, so it is one cache entry).
    const detailOccurrences = useOccurrences(programId, {
        dutyId: detail?.linkedDutyId ?? '',
        window: OccurrenceWindow.ALL
    }, {
        enabled: !!detail?.linkedDutyId
    }).data?.occurrences ?? [];
    const detailOccurrence = detail?.linkedOccurrenceId ? detailOccurrences.find((o)=>o.id === detail.linkedOccurrenceId) : undefined;
    const detailDutyLabel = detail?.linkedDutyId ? (()=>{
        const d = dutyById.get(detail.linkedDutyId);
        return d ? dutyLabel(d) : 'Linked (unavailable)';
    })() : '—';
    const detailOccurrenceLabel = !detail?.linkedOccurrenceId ? NO_DATE_LABEL : detailOccurrence ? occurrenceLabel(detailOccurrence) : 'Linked (unavailable)';
    // Detail sections: read values plus, per row, the inline-edit control. The
    // CLIN/deliverable/occurrence triad shares the SAME parent-controlled
    // pickers as the create modal (`editControl`, not `editField` — see
    // linkedClinId's declaration above for why).
    const detailSections = detail ? [
        {
            heading: 'Details',
            rows: [
                {
                    label: 'Type',
                    value: detail.type || '—',
                    editField: {
                        kind: 'select',
                        name: 'type',
                        label: 'Type',
                        required: true,
                        options: typeOptions,
                        optionHelp: typeOptionHelp,
                        optionHelpTestId: Testids.deliveryCompliance.deviationTypeHelp
                    }
                },
                {
                    label: 'Category',
                    value: detail.category || '—',
                    editField: {
                        kind: 'text',
                        name: 'category',
                        label: 'Category',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'State',
                    value: detail.state || '—',
                    editField: {
                        kind: 'select',
                        name: 'state',
                        label: 'State',
                        required: true,
                        options: stateOptions
                    }
                },
                {
                    label: 'Approval source',
                    value: detail.approvalSource || '—',
                    editField: {
                        kind: 'select',
                        name: 'approvalSource',
                        label: 'Approval source',
                        options: approvalSourceOptions,
                        helpText: WAIVERS_TAILORING_COPY.approvalSourceHelp
                    }
                },
                {
                    label: 'Approved by',
                    value: detailApprovedByName || '—',
                    editControl: approvalPicker
                },
                {
                    label: 'Approved replacement due date (recorded)',
                    value: formatLocalYmd(detail.approvedReplacementDueDate) ?? '—',
                    // Gated on the record's PERSISTED type: switching Type live via
                    // its own editField above doesn't retroactively show this
                    // control until the record is saved and the drawer reopens —
                    // an accepted simplification (see EntityFormAccess.watch, which
                    // isn't available at this row-config layer).
                    editControl: detail.type === 'WAIVER' ? replacementDueDatePicker : undefined
                },
                {
                    label: 'Answer received',
                    value: formatLocalYmd(detail.answeredAt) ?? '—',
                    // Unconditional (not gated on Type), unlike the replacement due
                    // date row above — every deviation can carry an answer date.
                    editControl: answeredAtPicker
                },
                {
                    label: 'Linked CLIN',
                    value: labelForOption(detail.linkedClinId, clinOptions),
                    editControl: linkedClinPicker
                },
                {
                    label: 'Linked deliverable',
                    value: detailDutyLabel,
                    editControl: linkedDeliverablePicker
                },
                {
                    label: 'Date',
                    value: detailOccurrenceLabel,
                    // Same gating precedent as "Approved replacement due date"
                    // above: reads the PERSISTED type, not a live watch. Nothing to
                    // list until a deliverable is picked.
                    editControl: detail.type === 'WAIVER' && linkedDutyId ? linkedOccurrencePicker : undefined
                },
                {
                    label: 'Approved cadence (recorded)',
                    value: detail.approvedCadence || 'No cadence change',
                    // A cadence needs a deliverable to apply to (see approvedCadencePicker).
                    editControl: detail.type === 'WAIVER' && (detail.linkedTaskId || linkedDutyId) ? approvedCadencePicker : undefined
                }
            ]
        },
        {
            heading: 'Comment',
            rows: [
                {
                    label: 'Comment',
                    value: detail.comment || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'comment',
                        label: 'Comment',
                        rows: 3
                    }
                }
            ]
        }
    ] : [];
    const newButton = /*#__PURE__*/ _jsx(SpecraButton, {
        onClick: openCreate,
        disabled: !canWrite,
        title: !canWrite ? PROGRAM_WRITE_DENIED.quality : undefined,
        "data-testid": Testids.deliveryCompliance.deviationCreate,
        children: "New Waiver or Tailoring"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        WAIVERS_TAILORING_COPY.label,
                        ' ',
                        /*#__PURE__*/ _jsxs("span", {
                            className: "font-normal text-muted-foreground",
                            children: [
                                "· ",
                                items.length
                            ]
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: WAIVERS_TAILORING_COPY.sectionInfo.map((part, i)=>'strong' in part && part.strong ? /*#__PURE__*/ _jsx("strong", {
                                    children: part.text
                                }, i) : part.text),
                            children: /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "About Waivers and Tailoring",
                                "data-testid": Testids.deliveryCompliance.deviationSectionInfo,
                                className: "inline-flex align-middle text-muted-foreground hover:text-foreground",
                                children: /*#__PURE__*/ _jsx(Info, {
                                    className: "h-4 w-4",
                                    "aria-hidden": true
                                })
                            })
                        })
                    ]
                }),
                description: "Departures from the delivery system — each needs sign-off from its approval source.",
                actions: newButton
            }),
            (items.length > 0 || awaiting > 0) && /*#__PURE__*/ _jsxs("p", {
                className: "text-sm text-muted-foreground",
                "data-testid": Testids.cmApprovals.waiversCount,
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: awaiting > 0 ? 'font-medium text-warning' : undefined,
                        children: [
                            awaiting,
                            " awaiting approval"
                        ]
                    }),
                    ' ',
                    "· ",
                    approved,
                    " approved · ",
                    denied,
                    " denied"
                ]
            }),
            items.length === 0 && !isLoading && (waivers.isLoading || waivers.count > 0) ? null : /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryCompliance.deviationsTable,
                rowTestid: Testids.deliveryCompliance.deviationRow,
                rowKey: (d)=>d.id,
                isLoading: isLoading,
                hideRowActions: !canWrite,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                sort: prefs.sort,
                onToggleSort: prefs.toggleSort,
                filters: prefs.filters,
                onFilterChange: prefs.setFilter,
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: WAIVERS_TAILORING_COPY.emptyTitle,
                    description: WAIVERS_TAILORING_COPY.emptyDescription,
                    action: !canWrite ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: openCreate,
                        children: "New Waiver or Tailoring"
                    })
                }),
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('read');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryCompliance.deviationDetails,
                                children: "Details"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode; the
                                // drawer's onEditStart seeds the approval picker from this record.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryCompliance.deviationEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryCompliance.deviationDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(DateWaiversSection, {
                programId: programId
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New Approved Change",
                ...deviationFormConfig(editing),
                // SAD-863: the approval evidence and its processing come first, so the
                // fields below fill from it rather than the other way round.
                topContent: (form)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-4 border-b pb-4",
                        "data-testid": Testids.deliveryCompliance.deviationExtractTop,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-medium",
                                        children: "Attachments"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        type: "file",
                                        multiple: true,
                                        "data-testid": Testids.deliveryCompliance.deviationFileInput,
                                        onChange: (e)=>{
                                            const picked = Array.from(e.target.files ?? []);
                                            if (picked.length) setStagedFiles((prev)=>[
                                                    ...prev,
                                                    ...picked
                                                ]);
                                            e.target.value = '';
                                        }
                                    }),
                                    stagedFiles.length > 0 && /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-1 text-xs text-muted-foreground",
                                        children: stagedFiles.map((f, i)=>/*#__PURE__*/ _jsxs("li", {
                                                className: "flex items-center justify-between gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "truncate",
                                                        children: f.name
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        className: "text-destructive hover:underline",
                                                        onClick: ()=>setStagedFiles((prev)=>prev.filter((_, j)=>j !== i)),
                                                        children: "remove"
                                                    })
                                                ]
                                            }, `${f.name}-${i}`))
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "Files attach to the record when you save."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between gap-2 rounded-md border bg-muted/10 p-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                        children: WAIVERS_TAILORING_COPY.processApprovalEvidenceHelp
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        type: "button",
                                        size: "sm",
                                        variant: "secondary",
                                        disabled: stagedFiles.length === 0 || processingApproval,
                                        onClick: ()=>void processApprovalEvidence(),
                                        "data-testid": Testids.deliveryCompliance.deviationProcessApprovalEvidence,
                                        children: processingApproval ? 'Processing…' : 'Process approval evidence'
                                    })
                                ]
                            }),
                            processApprovalError && /*#__PURE__*/ _jsx("p", {
                                className: "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive",
                                role: "alert",
                                children: processApprovalError
                            }),
                            deviationRun && deviationRun.warnings.length > 0 && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                tone: "warning",
                                "data-testid": Testids.deliveryCompliance.deviationRunWarnings,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-medium",
                                        children: WAIVERS_TAILORING_COPY.runWarningsIntro
                                    }),
                                    /*#__PURE__*/ _jsx("ul", {
                                        className: "mt-1 list-disc space-y-0.5 pl-4",
                                        children: deviationRun.warnings.map((w)=>/*#__PURE__*/ _jsx("li", {
                                                children: w
                                            }, w))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceFailureNotice, {
                                target: deviationIntelligenceTarget,
                                workflowKind: IntelligenceTypes.WorkflowKind.DEVIATION,
                                // Same pin as deviationRun/ProposalReview below (SAD-235): the
                                // shared (programId, 'Deviation', '') target means an unpinned
                                // notice would show — and offer Retry on — a failed run left
                                // behind by a different dialog session, not one this session
                                // started.
                                pinnedRunId: pinnedDeviationRunId
                            }),
                            /*#__PURE__*/ _jsx(ProposalReview, {
                                target: deviationIntelligenceTarget,
                                workflowKind: IntelligenceTypes.WorkflowKind.DEVIATION,
                                draftId: draftId,
                                // Same pin as deviationRun above (SAD-235): the panel must
                                // never show a different run than the one the warning/Save-gate
                                // logic reads, e.g. an older run left awaiting review by a
                                // different dialog session for this same shared target.
                                pinnedRunId: pinnedDeviationRunId,
                                fieldBindings: DEVIATION_FIELD_BINDINGS,
                                onApplyToDraft: (values)=>applyDeviationProposals(values, form.setValue, setAnsweredAt, setApproverNameSuggestion)
                            })
                        ]
                    }),
                extraContent: (form)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-4",
                        children: [
                            isWaiverType(form.watch('type')) && approvalPicker,
                            isWaiverType(form.watch('type')) && approverNameSuggestion && /*#__PURE__*/ _jsxs("p", {
                                className: "text-xs text-muted-foreground",
                                "data-testid": Testids.deliveryCompliance.deviationApproverNameSuggestion,
                                children: [
                                    WAIVERS_TAILORING_COPY.approverNameSuggestionPrefix,
                                    " “",
                                    approverNameSuggestion,
                                    "” ",
                                    WAIVERS_TAILORING_COPY.approverNameSuggestionSuffix
                                ]
                            }),
                            isWaiverType(form.watch('type')) && linkedClinPicker,
                            isWaiverType(form.watch('type')) && linkedDeliverablePicker,
                            showsOccurrence(form.watch('type'), form.watch('category')) && linkedDutyId && linkedOccurrencePicker,
                            showsCadence(form.watch('type'), form.watch('category')) && (legacyTaskId || linkedDutyId) && approvedCadencePicker,
                            showsOccurrence(form.watch('type'), form.watch('category')) && replacementDueDatePicker,
                            isWaiverType(form.watch('type')) && answeredAtPicker,
                            deviationShowUnverifiedSenderBanner && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "danger",
                                "data-testid": Testids.deliveryCompliance.deviationApprovalUnverifiedSender,
                                children: WAIVERS_TAILORING_COPY.unverifiedSenderWarning
                            }),
                            deviationApprovedStateHasUnverifiedWarning && form.watch('state') === 'Approved' && !approvalContactId && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "warning",
                                "data-testid": Testids.deliveryCompliance.deviationApprovalBlocked,
                                children: WAIVERS_TAILORING_COPY.approvalBlockedNotice
                            })
                        ]
                    }),
                onSubmit: async (v)=>{
                    // Same gate as the banner above, evaluated against the SUBMITTED
                    // values (v.state) rather than form.watch — an email alone must
                    // never be able to save a verified approval without an explicit
                    // contact pick, regardless of how state got to 'Approved'.
                    if (deviationApprovedStateHasUnverifiedWarning && v.state === 'Approved' && !approvalContactId) {
                        throw new Error(WAIVERS_TAILORING_COPY.approvalBlockedNotice);
                    }
                    await submitDeviation(v, editing?.id ?? '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Approved Change",
                description: deleting ? `Delete ${deleting.type} for "${deleting.category}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? `${typeLabel(detail.type)} — ${detail.category}` : 'Approved Change',
                sections: detailSections,
                testid: Testids.deliveryCompliance.deviationDetailDrawer,
                edit: detailEdit,
                canEdit: canWrite,
                initialMode: detailMode,
                children: [
                    detail?.linkedOccurrenceId && /*#__PURE__*/ _jsx(LinkedDatePanel, {
                        programId: programId,
                        deviation: detail,
                        occurrence: detailOccurrence,
                        canEdit: cm.canWrite
                    }, `${detail.id}:${detail.linkedOccurrenceId}`),
                    detail && cm.canWrite && isWaiverType(detail.type) && detail.state === 'Approved' && detail.approvedCadence && detail.linkedDutyId ? /*#__PURE__*/ _jsx(CadenceTeamRulePanel, {
                        programId: programId,
                        deviation: detail
                    }, detail.id) : null,
                    detail && canWrite && /*#__PURE__*/ _jsx("div", {
                        className: "border-t pt-4",
                        children: /*#__PURE__*/ _jsx(DocumentsPanel, {
                            entityType: "deviation",
                            entityId: detail.id,
                            title: "Attachments"
                        })
                    })
                ]
            })
        ]
    });
}
// The linked date's side of a Government Approved Change (D113): its status,
// its waiver decision, and the two acts this record can start on it. A date
// is excused only through the one two-person path (D111, D112): this proposes
// the waiver, and a second person approves or rejects it on the date itself.
function LinkedDatePanel({ programId, deviation, occurrence, canEdit }) {
    const occurrenceId = deviation.linkedOccurrenceId;
    const decisions = useOccurrenceDecisions(programId, {
        occurrenceId
    }).data?.decisions ?? [];
    const names = useUserNames();
    const propose = useProposeWaiver(programId, {
        success: 'Waiver proposed. A second person must approve it.'
    });
    const move = useMoveOccurrence(programId, {
        success: 'Date moved'
    });
    // SAD-834: a date that dates an event moves only by an approved extension.
    const extend = useProposeExtension(programId, {
        success: 'Extension proposed. A second person must approve it.'
    });
    const [proposing, setProposing] = useState(false);
    // The government's own date when this record has one; never today by
    // default — the person picks it, or takes the suggestion.
    const [waivedOn, setWaivedOn] = useState(formatLocalYmd(deviation.answeredAt) ?? '');
    const [reason, setReason] = useState(deviationReason(deviation));
    const status = occurrence ? occurrenceStatus(occurrence) : undefined;
    const closed = !!status && [
        'waived',
        'not_owed',
        'retired'
    ].includes(status.state);
    const open = decisions.find((d)=>d.state === 'UnderReview');
    // Added waiver evidence is not a decision about the date itself (SAD-1010).
    const latest = decisions.find((d)=>d.kind !== 'waiver_evidence');
    const replacement = formatLocalYmd(deviation.approvedReplacementDueDate) ?? '';
    const dateHref = `/delivery/programs/${programId}/deliverables?occ=${occurrenceId}`;
    // A date is waived only on a change the government has already approved
    // (D114 #3); before that the panel says what comes first.
    const couldPropose = canEdit && isWaiverType(deviation.type) && !!occurrence && !open && !closed;
    const canPropose = couldPropose && deviation.state === 'Approved';
    const canMove = canEdit && !!occurrence && !closed && !!replacement && replacement !== occurrence.dueOn;
    const eventDate = !!occurrence?.event && !occurrence.event.projected;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 pt-4",
        "data-testid": Testids.cmCompliance.deviationDate,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Linked date",
                action: /*#__PURE__*/ _jsx(Link, {
                    to: dateHref,
                    className: specraButtonVariants({
                        variant: 'secondary',
                        size: 'sm'
                    }),
                    "data-testid": Testids.cmCompliance.deviationOpenDate,
                    children: "Open the date"
                })
            }),
            occurrence && status ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-[length:var(--fs-ui-body)]",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            occurrence.label || cmMonth(occurrence.nominalDueOn || occurrence.dueOn),
                            " · due",
                            ' ',
                            cmDate(occurrence.dueOn, 'not stated')
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CmStatusBadge, {
                        status: status
                    })
                ]
            }) : /*#__PURE__*/ _jsx("p", {
                className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                children: "This date is no longer on the deliverable’s schedule."
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                "data-testid": Testids.cmCompliance.deviationDecision,
                children: decisionLine(latest, names)
            }),
            canMove && occurrence ? /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "secondary",
                disabled: move.isPending || extend.isPending || eventDate && !!open,
                onClick: ()=>{
                    const b = {
                        occurrenceId,
                        dueOn: replacement,
                        reason: deviationReason(deviation) || 'Government approved change'
                    };
                    if (eventDate) extend.mutate(b);
                    else move.mutate(b);
                },
                "data-testid": Testids.cmCompliance.deviationMoveDate,
                children: [
                    eventDate ? 'Propose extending the date to' : 'Move the date to',
                    " ",
                    cmDate(replacement)
                ]
            }) : null,
            couldPropose && !canPropose ? /*#__PURE__*/ _jsx("p", {
                className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                "data-testid": Testids.cmCompliance.deviationWaiverNeedsApproval,
                children: "Approve this change first; then propose waiving the date."
            }) : null,
            canPropose && !proposing ? /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                onClick: ()=>setProposing(true),
                "data-testid": Testids.cmCompliance.deviationProposeWaiver,
                children: "Propose waiving this date"
            }) : null,
            canPropose && proposing ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3 rounded-md border p-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        picker: "native",
                        label: "Waived by the COR on",
                        value: waivedOn,
                        onValueChange: setWaivedOn,
                        hint: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                "Optional; leave blank if the date was not recorded.",
                                ' ',
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    className: "font-medium text-foreground underline",
                                    onClick: ()=>setWaivedOn(todayISO()),
                                    "data-testid": Testids.cmCompliance.deviationUseToday,
                                    children: "Use today’s date"
                                })
                            ]
                        }),
                        "data-testid": Testids.cmCompliance.deviationWaivedOn
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "The COR’s reason",
                        required: true,
                        hint: "A second person reads this before approving.",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            rows: 2,
                            value: reason,
                            onChange: (e)=>setReason(e.target.value),
                            "data-testid": Testids.cmCompliance.deviationWaiverReason
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !reason.trim() || propose.isPending,
                                onClick: ()=>propose.mutate({
                                        occurrenceId,
                                        waivedOn,
                                        reason: reason.trim()
                                    }, {
                                        onSuccess: ()=>setProposing(false)
                                    }),
                                "data-testid": Testids.cmCompliance.deviationWaiverSubmit,
                                children: "Propose"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setProposing(false),
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            }) : null
        ]
    });
}
// Where a team rule applied from a change says it came from: the change as a
// person reads it, never its id. It is also how a second apply is recognised.
function citedLocator(d) {
    const answered = formatLocalYmd(d.answeredAt);
    return [
        'Approved change (Deviations)',
        d.category,
        answered ? `answered ${cmDate(answered)}` : ''
    ].filter(Boolean).join(' · ');
}
// Two changes of one category answered the same day share a locator, so the
// applied rule is recognised by what it carries from this change: the
// locator, the quoted comment and the cadence.
const citesChange = (a, rule)=>a.teamRules.some((r)=>r.sourceLocator === rule.sourceLocator && r.ruleText === rule.ruleText && r.cadence === rule.cadence);
// An existing team rule, re-sent exactly as stored (SetDutyAssignment replaces
// the whole set; the draft's `base` carries the columns no editor shows).
function keptTeamRule(r) {
    return {
        id: r.id,
        fields: {
            ...fieldsFromDraft(draftFromRule(r, true)),
            gate: 'sow'
        }
    };
}
// The approved cadence as one new recurring team rule on the SOW. Its first
// date is the award's (the first award rule that is not event-driven); with
// none it states no start and reads "needs a start date" until one is stated.
function citedTeamRule(d, cadence, award, locator) {
    const hasOffset = !!award?.hasOffset && !award.dueOn;
    return {
        kind: 'recurring',
        gate: 'sow',
        periodOfPerformanceId: '',
        optionalTaskId: '',
        dueOn: award?.dueOn ?? '',
        hasOffset,
        offsetN: hasOffset ? award.offsetN : 0,
        offsetUnit: hasOffset ? award.offsetUnit : '',
        cadence,
        cadenceInterval: 0,
        dayOrdinals: [],
        dayWeekday: '',
        dayBasis: '',
        eventKind: '',
        subjects: [],
        ruleText: (d.comment ?? '').trim(),
        sourceLocator: locator,
        clinId: '',
        // A rule's source document is a program document; this change's approval
        // lives in its own attachments, so nothing is claimed here.
        sourceDocumentId: '',
        effectiveFrom: '',
        effectiveTo: '',
        establishedByModificationId: ''
    };
}
// An approved cadence change applies as a team rule on the SOW that performs
// the linked deliverable, citing this change (D114 #4); never a modification.
function CadenceTeamRulePanel({ programId, deviation }) {
    const duty = useDuty(programId, deviation.linkedDutyId);
    // Every team's SOWs, in one ListWorkstreams read (SAD-994).
    const details = useWorkstreamDetails(programId);
    const save = useSetDutyAssignment(programId, {
        errors: 'inline'
    });
    const [open, setOpen] = useState(false);
    const [pickedSowId, setPickedSowId] = useState('');
    const [receipt, setReceipt] = useState(null);
    const cadence = teamRuleCadence(deviation.approvedCadence);
    const locator = citedLocator(deviation);
    const sowLabel = new Map();
    for (const ws of details.data ?? []){
        for (const sow of ws.sows){
            sowLabel.set(sow.id, `${ws?.workstream?.name || 'Unnamed team'} · ${sow.title || 'Untitled SOW'}${sow.version ? ` v${sow.version}` : ''}`);
        }
    }
    const performing = (duty.data?.assignments ?? []).filter((a)=>a.handling === 'performed');
    const sowId = pickedSowId || (performing.length === 1 ? performing[0].sowId : '');
    const target = performing.find((a)=>a.sowId === sowId);
    const award = duty.data?.rules.find((r)=>r.kind !== 'event');
    const fields = cadence ? citedTeamRule(deviation, cadence, award, locator) : undefined;
    const allApplied = !!fields && performing.length > 0 && performing.every((a)=>citesChange(a, fields));
    const label = (a)=>sowLabel.get(a.sowId) ?? 'SOW';
    const apply = ()=>{
        if (!target || !fields) return;
        save.mutate({
            sowId: target.sowId,
            dutyId: target.dutyId,
            handling: target.handling,
            routedToWorkstreamId: target.routedToWorkstreamId,
            notOwedReason: target.notOwedReason,
            titleAsStated: target.titleAsStated,
            senderContactId: target.senderContactId,
            recipient: target.recipient,
            format: target.format,
            submissionMethod: target.submissionMethod,
            acceptanceCriteria: target.acceptanceCriteria,
            overrideLocators: target.overrideLocators.map((l)=>({
                    field: l.field,
                    sourceLocator: l.sourceLocator,
                    quote: l.quote
                })),
            sourceLocator: target.sourceLocator,
            note: target.note,
            teamRules: [
                ...target.teamRules.map(keptTeamRule),
                {
                    id: '',
                    fields
                }
            ]
        }, {
            onSuccess: (r)=>setReceipt(`Team rule added to ${label(target)}. ${mintReceipt(r.result?.mint)}.`)
        });
    };
    const muted = 'text-[length:var(--fs-ui-body)] text-muted-foreground';
    let body;
    if (!cadence) {
        body = /*#__PURE__*/ _jsxs("p", {
            className: muted,
            "data-testid": Testids.cmCompliance.deviationTeamRuleRefused,
            children: [
                "“",
                deviation.approvedCadence,
                "” is not a schedule a team rule can follow. Add the team rule on the team’s page instead."
            ]
        });
    } else if (!fields?.ruleText) {
        // Every rule keeps its verbatim sentence (pangaea requires rule_text);
        // for this rule that sentence is the change's own comment.
        body = /*#__PURE__*/ _jsx("p", {
            className: muted,
            "data-testid": Testids.cmCompliance.deviationTeamRuleNeedsComment,
            children: "Add a comment saying what was approved; the team rule quotes it."
        });
    } else if (duty.isLoading) {
        body = /*#__PURE__*/ _jsx("p", {
            className: muted,
            children: "Loading…"
        });
    } else if (duty.error) {
        body = /*#__PURE__*/ _jsx("p", {
            className: "text-[length:var(--fs-ui-body)] text-danger",
            role: "alert",
            children: apiErrorMessage(duty.error, 'Could not load the deliverable.')
        });
    } else if (performing.length === 0) {
        body = /*#__PURE__*/ _jsxs("p", {
            className: muted,
            "data-testid": Testids.cmCompliance.deviationTeamRuleNoSow,
            children: [
                "No SOW performs this deliverable yet — handle it on the team page first.",
                ' ',
                /*#__PURE__*/ _jsx(Link, {
                    to: `/delivery/programs/${programId}/workstreams`,
                    className: "font-medium text-foreground underline",
                    children: "Open Teams"
                })
            ]
        });
    } else if (receipt) {
        body = /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            tone: "success",
            variant: "framed",
            "data-testid": Testids.cmCompliance.deviationTeamRuleReceipt,
            children: receipt
        });
    } else if (allApplied) {
        body = /*#__PURE__*/ _jsxs("p", {
            className: muted,
            "data-testid": Testids.cmCompliance.deviationTeamRuleApplied,
            children: [
                "Already applied: ",
                performing.map(label).join(', '),
                ' ',
                performing.length === 1 ? 'has' : 'each have',
                " a team rule citing this change."
            ]
        });
    } else if (!open) {
        body = /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            variant: "secondary",
            onClick: ()=>setOpen(true),
            "data-testid": Testids.cmCompliance.deviationTeamRuleStart,
            children: "Apply as a team rule…"
        });
    } else {
        const applied = !!target && !!fields && citesChange(target, fields);
        body = /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3 rounded-md border p-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "SOW that performs it",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: sowId,
                        onChange: (e)=>setPickedSowId(e.target.value),
                        "data-testid": Testids.cmCompliance.deviationTeamRuleSow,
                        children: [
                            performing.length > 1 ? /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the SOW…"
                            }) : null,
                            performing.map((a)=>/*#__PURE__*/ _jsx("option", {
                                    value: a.sowId,
                                    children: label(a)
                                }, a.sowId))
                        ]
                    })
                }),
                fields ? /*#__PURE__*/ _jsxs("p", {
                    className: muted,
                    children: [
                        "→ ",
                        describeRule(fields),
                        !fields.dueOn && !fields.hasOffset ? '. The award states no first date, so this rule needs a start date before it adds any dates.' : ''
                    ]
                }) : null,
                applied ? /*#__PURE__*/ _jsx("p", {
                    className: muted,
                    "data-testid": Testids.cmCompliance.deviationTeamRuleApplied,
                    children: "Already applied: this SOW has a team rule citing this change."
                }) : null,
                save.error ? /*#__PURE__*/ _jsx("p", {
                    className: "text-[length:var(--fs-ui-body)] text-danger",
                    role: "alert",
                    children: apiErrorMessage(save.error, 'Could not add the team rule')
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            disabled: !target || applied || save.isPending,
                            isLoading: save.isPending,
                            onClick: apply,
                            "data-testid": Testids.cmCompliance.deviationTeamRuleConfirm,
                            children: "Add the team rule"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "ghost",
                            onClick: ()=>setOpen(false),
                            children: "Cancel"
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 pt-4",
        "data-testid": Testids.cmCompliance.deviationTeamRule,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Approved cadence"
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: muted,
                children: [
                    "This change approved a ",
                    deviation.approvedCadence.toLowerCase(),
                    " schedule. It applies as a team rule on the SOW that performs the deliverable, citing this change."
                ]
            }),
            body
        ]
    });
}
// The date's latest decision in one sentence. Approve and Reject live on the
// date itself (the Open the date link), never here.
function decisionLine(d, names) {
    if (!d) return 'No waiver has been proposed for this date.';
    const what = d.kind === 'waiver' ? 'Waiver' : d.kind === 'extension' ? `Extension to ${cmDate(d.dueOn)}` : 'Not owed';
    switch(d.state){
        case 'UnderReview':
            return `${what} proposed by ${names(d.proposedByUserId)} on ${cmDate(instantDay(d.proposedAt))}, ${d.step === 'proposed' ? 'awaiting the COR’s answer' : 'awaiting a second person’s approval'}.`;
        case 'Applied':
            if (d.kind === 'extension') return `Extended to ${cmDate(d.dueOn)}, approved by ${names(d.decidedByUserId || d.approvedByUserId)}.`;
            return d.kind === 'waiver' ? `Waived by the COR on ${d.waivedOn ? cmDate(d.waivedOn) : 'a date not recorded'}${d.reason ? `: “${d.reason}”` : ''}.` : `Marked not owed, approved by ${names(d.decidedByUserId || d.approvedByUserId)}.`;
        case 'Rejected':
            return `${d.step === 'cor_declined' ? 'The COR declined' : 'Rejected'}: ${d.stateReason || 'no reason given'}.`;
        case 'Cancelled':
            return `${what} withdrawn: ${d.stateReason || 'no reason given'}.`;
        default:
            return `${what} ${d.state.toLowerCase()}.`;
    }
}
